import type { Accessor, Setter } from 'solid-js'
import { createSignal } from 'solid-js'

type SignalPair<T> = [Accessor<T>, Setter<T>]

interface RegisterSWOptions {
	immediate?: boolean
	onNeedRefresh?: () => void
	onOfflineReady?: () => void
	onRegistered?: (registration: ServiceWorkerRegistration | undefined) => void
	onRegisteredSW?: (swUrl: string, registration: ServiceWorkerRegistration | undefined) => void
	onRegisterError?: (error: unknown) => void
}

interface RegisterSWResult {
	needRefresh: SignalPair<boolean>
	offlineReady: SignalPair<boolean>
	updateServiceWorker: (reloadPage?: boolean) => Promise<void>
}

const SW_URL = import.meta.env.DEV ? './dev-sw.js?dev-sw' : './sw.js'
const SW_SCOPE = './'
const SW_TYPE: WorkerType = 'module'

function registerSW(options: RegisterSWOptions = {}) {
	const {
		immediate = false,
		onNeedRefresh,
		onOfflineReady,
		onRegistered,
		onRegisteredSW,
		onRegisterError,
	} = options

	let wb: import('workbox-window').Workbox | undefined
	let registerPromise: Promise<void> | undefined
	let sendSkipWaitingMessage: (() => void) | undefined

	const updateServiceWorker = async (_reloadPage = true) => {
		await registerPromise
		sendSkipWaitingMessage?.()
	}

	async function register() {
		if (!('serviceWorker' in navigator)) return
		// Skip SW registration in dev: VitePWA `devOptions.enabled` is false in vite.config.ts,
		// so `dev-sw.js?dev-sw` 404s into the SPA index.html and registration fails with a
		// MIME-type SecurityError. Remove this guard when devOptions is re-enabled.
		if (import.meta.env.DEV) return

		wb = await import('workbox-window')
			.then(({ Workbox }) => new Workbox(SW_URL, { scope: SW_SCOPE, type: SW_TYPE }))
			.catch((error) => {
				onRegisterError?.(error)
				return undefined
			})

		if (!wb) return

		sendSkipWaitingMessage = () => {
			wb?.messageSkipWaiting()
		}

		let onNeedRefreshCalled = false
		const showSkipWaitingPrompt = () => {
			onNeedRefreshCalled = true
			wb?.addEventListener('controlling', (event) => {
				if (event.isUpdate) {
					window.location.reload()
				}
			})
			onNeedRefresh?.()
		}

		wb.addEventListener('installed', (event) => {
			if (typeof event.isUpdate === 'undefined') {
				if (typeof event.isExternal !== 'undefined') {
					if (event.isExternal) {
						showSkipWaitingPrompt()
					} else if (!onNeedRefreshCalled) {
						onOfflineReady?.()
					}
				} else if (!onNeedRefreshCalled) {
					onOfflineReady?.()
				}
			} else if (!event.isUpdate) {
				onOfflineReady?.()
			}
		})
		wb.addEventListener('waiting', showSkipWaitingPrompt)

		await wb.register({ immediate })
			.then((registration) => {
				if (onRegisteredSW) {
					onRegisteredSW(SW_URL, registration)
				} else {
					onRegistered?.(registration)
				}
			})
			.catch((error) => {
				onRegisterError?.(error)
			})
	}

	registerPromise = register()

	return updateServiceWorker
}

export function useRegisterSW(options: RegisterSWOptions = {}): RegisterSWResult {
	const {
		immediate = true,
		onNeedRefresh,
		onOfflineReady,
		onRegistered,
		onRegisteredSW,
		onRegisterError,
	} = options

	const [needRefresh, setNeedRefresh] = createSignal(false)
	const [offlineReady, setOfflineReady] = createSignal(false)

	const updateServiceWorker = registerSW({
		immediate,
		onOfflineReady() {
			setOfflineReady(true)
			onOfflineReady?.()
		},
		onNeedRefresh() {
			setNeedRefresh(true)
			onNeedRefresh?.()
		},
		onRegistered,
		onRegisteredSW,
		onRegisterError,
	})

	return {
		needRefresh: [needRefresh, setNeedRefresh],
		offlineReady: [offlineReady, setOfflineReady],
		updateServiceWorker,
	}
}
